import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FileText, Plus, Users, Trash2, Edit2, Printer, Share2, Search, Loader2, X, Package, UploadCloud, Download, ArrowLeft, Sparkles } from 'lucide-react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CQ = `${API}/customer-quotations`;

const money = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
// jsPDF's default Helvetica font has no glyph for ₹ (U+20B9) so the symbol
// renders as a corrupted "¹" and shifts subsequent characters. Use plain
// "Rs." in PDFs.
const moneyPdf = (n) => `Rs. ${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// ────── Watermark helper: overlay end-customer info on JP product images ──────
// Loads an image URL (with CORS), draws it onto a canvas, then writes the
// watermark text diagonally across to deter screenshot theft. Returns a
// data-URL. Resolves to null on any failure (timeout, CORS, load error)
// so the caller can render the row without an image instead of hanging.
async function watermarkImage(srcUrl, watermarkText) {
  return new Promise((resolve) => {
    if (!srcUrl) { resolve(null); return; }
    const img = new Image();
    img.crossOrigin = 'anonymous';
    let settled = false;
    const done = (v) => { if (!settled) { settled = true; resolve(v); } };
    // Hard timeout — never let one bad image stall the PDF
    setTimeout(() => done(null), 8000);
    img.onload = () => {
      try {
        const W = 240, H = Math.round(240 * (img.height / Math.max(1, img.width)));
        const c = document.createElement('canvas');
        c.width = W; c.height = H;
        const ctx = c.getContext('2d');
        ctx.drawImage(img, 0, 0, W, H);
        // Diagonal repeated watermark
        ctx.save();
        ctx.font = 'bold 14px sans-serif';
        ctx.fillStyle = 'rgba(180, 30, 30, 0.28)';
        ctx.translate(W / 2, H / 2);
        ctx.rotate(-Math.PI / 6);
        const text = (watermarkText || '').toUpperCase().substring(0, 28);
        const m = ctx.measureText(text);
        for (let y = -H; y < H; y += 32) {
          for (let x = -W; x < W; x += Math.max(60, m.width + 18)) {
            ctx.fillText(text, x, y);
          }
        }
        ctx.restore();
        done(c.toDataURL('image/jpeg', 0.82));
      } catch (_e) { done(null); }
    };
    img.onerror = () => done(null);
    img.src = srcUrl;
  });
}

// Resolve a part's image into a fetchable URL or data-URL.
// Handles three storage shapes: full http(s) URL, "data:..." base64, or
// a bare filename (served by GET /api/images/part/{filename}).
function resolvePartImageSrc(image) {
  if (!image) return null;
  const s = String(image);
  if (s.startsWith('http://') || s.startsWith('https://') || s.startsWith('data:')) return s;
  return `${process.env.REACT_APP_BACKEND_URL}/api/images/part/${encodeURIComponent(s)}`;
}

// Build a Tally-style quotation PDF. When `withImages=true` and the
// quotation is JP-sourced, every line's product image is watermarked with
// the end-customer's name and embedded above its row.
// `opts.parent` lets the caller pass a live user record so the letterhead
// (name + address + mobile) always reflects the current parent customer
// even on older quotations that did not stamp these fields.
async function buildQuotationPdf(quotation, opts = {}) {
  // With-images PDF is enabled if any line has a JP image (works for both
  // pure-JP and mixed quotations).
  const hasAnyJpImage = (quotation.lines || []).some((ln) => (ln.source_type === 'jp' || ln.jp_part_no) && ln.image);
  const withImages = !!opts.withImages && hasAnyJpImage;
  const parent = opts.parent || {};
  const doc = new jsPDF();

  // ── Centered letterhead: Parent (sender) name → address → mobile ──
  const CENTER_X = 105;
  const parentName = (parent.company_name || quotation.parent_company_name || parent.name || quotation.parent_customer_name || '').trim();
  const parentAddress = (parent.address || '').trim();
  const parentMobile = (parent.mobile || quotation.parent_customer_mobile || '').trim();

  let headY = 14;
  if (parentName) {
    doc.setFontSize(14); doc.setFont('helvetica', 'bold');
    doc.text(parentName, CENTER_X, headY, { align: 'center' });
    headY += 6;
  }
  doc.setFont('helvetica', 'normal'); doc.setFontSize(9);
  if (parentAddress) {
    // Wrap long addresses to 2 lines if needed
    const wrapped = doc.splitTextToSize(parentAddress, 170);
    wrapped.forEach((ln) => { doc.text(ln, CENTER_X, headY, { align: 'center' }); headY += 4; });
  }
  if (parentMobile) {
    doc.text(`Mobile: ${parentMobile}`, CENTER_X, headY, { align: 'center' });
    headY += 4;
  }

  // ── QUOTATION badge + No + Date (right edge, aligned vertically with top) ──
  doc.setFontSize(16); doc.setFont('helvetica', 'bold'); doc.setTextColor(180, 30, 30);
  doc.text('QUOTATION', 196, 14, { align: 'right' });
  doc.setFontSize(9); doc.setFont('helvetica', 'normal'); doc.setTextColor(0, 0, 0);
  doc.text(`No: ${quotation.quotation_id}`, 196, 20, { align: 'right' });
  doc.text(`Date: ${new Date(quotation.created_at).toLocaleDateString('en-IN')}`, 196, 25, { align: 'right' });

  const separatorY = Math.max(30, headY + 2);
  doc.setDrawColor(180, 180, 180);
  doc.line(14, separatorY, 196, separatorY);

  // ── Left-aligned "To:" block (end-customer) — original layout ──
  // The internal discount % is intentionally NOT shown on the PDF — the
  // end-customer only sees the final discounted price per line.
  let toY = separatorY + 6;
  doc.setFontSize(9); doc.setFont('helvetica', 'bold');
  doc.text('To:', 14, toY);
  doc.setFont('helvetica', 'normal');
  toY += 5;
  if (quotation.end_customer_company) { doc.text(quotation.end_customer_company, 14, toY); toY += 5; }
  if (quotation.end_customer_name && quotation.end_customer_name !== quotation.end_customer_company) {
    doc.text(quotation.end_customer_name, 14, toY); toY += 5;
  }
  const ecAddr = [quotation.end_customer_city, quotation.end_customer_state, quotation.end_customer_pin_code]
    .map((s) => (s || '').toString().trim()).filter(Boolean).join(', ');
  if (ecAddr) { doc.text(ecAddr, 14, toY); toY += 5; }
  if (quotation.end_customer_mobile) { doc.text(`Mob: ${quotation.end_customer_mobile}`, 14, toY); toY += 5; }

  const tableStartY = Math.max(toY + 4, separatorY + 14);

  const body = (quotation.lines || []).map((ln, i) => [
    String(i + 1),
    withImages ? '' : '',
    ln.source_type === 'oe' ? (ln.oe_part_no || '') : (ln.oe_part_no || ln.jp_part_no || ''),
    (ln.part_name || '').substring(0, 45),
    String(ln.quantity),
    moneyPdf(ln.basic_price),
    moneyPdf(ln.line_basic),
  ]);

  // Pre-watermark images if requested. The watermark identifies the SENDER
  // (the logged-in B2B customer) so that if the recipient screenshots the
  // quotation, the source of the leak is obvious. We do NOT use the
  // end-customer's name here — that would attribute the leak to the wrong
  // party and remove the sender's branding.
  const watermarkText = parent.company_name || parent.name || quotation.parent_company_name || quotation.parent_customer_name || 'CONFIDENTIAL';
  let lineImages = [];
  if (withImages) {
    lineImages = await Promise.all(
      (quotation.lines || []).map((ln) => {
        const src = resolvePartImageSrc(ln.image);
        return src ? watermarkImage(src, watermarkText) : Promise.resolve(null);
      })
    );
  }

  const columnStyles = withImages ? {
    0: { cellWidth: 8, halign: 'center' },
    1: { cellWidth: 22, halign: 'center', minCellHeight: 22 },
    2: { cellWidth: 28 },
    3: { cellWidth: 56 },
    4: { cellWidth: 12, halign: 'center' },
    5: { cellWidth: 24, halign: 'right' },
    6: { cellWidth: 26, halign: 'right' },
  } : {
    0: { cellWidth: 8, halign: 'center' },
    1: { cellWidth: 0 },
    2: { cellWidth: 32 },
    3: { cellWidth: 70 },
    4: { cellWidth: 14, halign: 'center' },
    5: { cellWidth: 28, halign: 'right' },
    6: { cellWidth: 30, halign: 'right' },
  };

  autoTable(doc, {
    startY: tableStartY,
    head: [['#', withImages ? 'Image' : '', 'Part Number', 'Description', 'Qty', 'Basic Price', 'Amount']],
    body,
    theme: 'grid',
    headStyles: { fillColor: [180, 30, 30], fontSize: 9, fontStyle: 'bold' },
    bodyStyles: { fontSize: 8, minCellHeight: withImages ? 22 : 6 },
    columnStyles,
    didDrawCell: (data) => {
      if (withImages && data.section === 'body' && data.column.index === 1) {
        const img = lineImages[data.row.index];
        if (img) {
          const pad = 1;
          const w = data.cell.width - pad * 2;
          const h = data.cell.height - pad * 2;
          try { doc.addImage(img, 'JPEG', data.cell.x + pad, data.cell.y + pad, w, h); }
          catch (_e) { /* image embed failed — leave blank */ }
        }
      }
    },
  });

  const endY = doc.lastAutoTable.finalY + 6;
  doc.setFontSize(9); doc.setFont('helvetica', 'normal');
  doc.text('Total Basic Amount', 130, endY); doc.text(moneyPdf(quotation.total_basic), 196, endY, { align: 'right' });
  doc.text('GST @ 18%', 130, endY + 5); doc.text(moneyPdf(quotation.total_gst), 196, endY + 5, { align: 'right' });
  doc.setFont('helvetica', 'bold'); doc.setFontSize(11);
  doc.text('Grand Total', 130, endY + 12); doc.text(moneyPdf(quotation.grand_total), 196, endY + 12, { align: 'right' });

  doc.setFont('helvetica', 'italic'); doc.setFontSize(7); doc.setTextColor(140, 140, 140);
  doc.text('Prices include 18% GST. This quotation is valid for 7 days. Subject to stock availability.', 105, 285, { align: 'center' });
  return doc;
}

// ────── Components ──────
const EndCustomerForm = ({ initial, onClose, onSaved, token }) => {
  const [form, setForm] = useState({
    name: '', company_name: '', mobile: '', city: '', state: '', pin_code: '',
    default_oe_discount_pct: 0, default_jp_discount_pct: 0,
    ...(initial || {}),
  });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!form.name?.trim() || !form.company_name?.trim()) { toast.error('Name and Company are required'); return; }
    setSaving(true);
    try {
      const url = initial?.end_customer_id ? `${CQ}/my-customers/${initial.end_customer_id}` : `${CQ}/my-customers`;
      const fn = initial?.end_customer_id ? axios.put : axios.post;
      const r = await fn(url, form, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(initial?.end_customer_id ? 'Sub-customer updated' : 'Sub-customer added');
      onSaved(r.data.end_customer || form);
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md bg-white">
        <DialogHeader><DialogTitle>{initial?.end_customer_id ? 'Edit Sub-Customer' : 'New Sub-Customer'}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Name *</Label><Input data-testid="ec-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          <div><Label>Company *</Label><Input data-testid="ec-company" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} /></div>
          <div><Label>Mobile</Label><Input data-testid="ec-mobile" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} /></div>
          <div className="grid grid-cols-3 gap-2">
            <div><Label>City</Label><Input data-testid="ec-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
            <div><Label>State</Label><Input data-testid="ec-state" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} /></div>
            <div><Label>PIN</Label><Input data-testid="ec-pin" value={form.pin_code} onChange={(e) => setForm({ ...form, pin_code: e.target.value })} /></div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Default OE discount %</Label>
              <Input data-testid="ec-oe-disc" type="number" min={0} max={99} step={0.5}
                value={form.default_oe_discount_pct ?? 0}
                onChange={(e) => setForm({ ...form, default_oe_discount_pct: Number(e.target.value) || 0 })} />
            </div>
            <div>
              <Label>Default JP discount %</Label>
              <Input data-testid="ec-jp-disc" type="number" min={0} max={99} step={0.5}
                value={form.default_jp_discount_pct ?? 0}
                onChange={(e) => setForm({ ...form, default_jp_discount_pct: Number(e.target.value) || 0 })} />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={submit} disabled={saving} data-testid="ec-save-btn">{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Save</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const PartPicker = ({ source, discountPct, onPicked, token }) => {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);
  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (q.trim().length < 2) { setRows([]); return; }
    setLoading(true);
    timer.current = setTimeout(async () => {
      try {
        const r = source === 'oe'
          ? await axios.get(`${CQ}/search-oe-parts`, { params: { q: q.trim(), limit: 20 }, headers: { Authorization: `Bearer ${token}` } })
          : await axios.get(`${API}/categories/All%20Products/parts`, { params: { search: q.trim(), limit: 20 }, headers: { Authorization: `Bearer ${token}` } });
        setRows(source === 'oe' ? (r.data?.results || []) : (r.data?.parts || []));
      } catch { setRows([]); }
      setLoading(false);
    }, 220);
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [q, source, token]);
  return (
    <div className="space-y-2" data-testid="cq-part-picker">
      <div className="relative">
        <Search className="absolute left-2 top-2.5 w-4 h-4 text-gray-400" />
        <Input data-testid="cq-search" placeholder={source === 'oe' ? 'Search OE part no / name' : 'Search JP part no / OE ref / name'} value={q} onChange={(e) => setQ(e.target.value)} className="pl-8" />
      </div>
      {loading && <div className="text-xs text-gray-500">Searching…</div>}
      <div className="max-h-72 overflow-y-auto space-y-1">
        {rows.map((p) => {
          const partNo = source === 'oe' ? p.oe_part_no : (p.new_jp_part_no || p.jp_part_no);
          const mrp = p.mrp || 0;
          const sell = mrp * (1 - (Number(discountPct) || 0) / 100);
          return (
            <button key={partNo} type="button" onClick={() => onPicked(p)} className="w-full text-left border rounded p-2 hover:bg-amber-50 hover:border-amber-300 transition" data-testid={`cq-pick-${partNo}`}>
              <div className="flex justify-between items-start gap-2">
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-xs font-semibold">{partNo}</div>
                  <div className="text-xs text-gray-700 truncate">{p.part_name}</div>
                  {source === 'jp' && p.oe_part_no && <div className="text-[10px] text-gray-500 truncate">OE: {p.oe_part_no}</div>}
                </div>
                <div className="text-right text-xs">
                  <div className="text-gray-400 line-through">MRP {money(mrp)}</div>
                  <div className="font-bold text-green-700">{money(sell)}</div>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default function CustomerQuotationsPage() {
  const { token, user } = useAuth();
  const [tab, setTab] = useState('create');
  const [endCustomers, setEndCustomers] = useState([]);
  const [myQuotations, setMyQuotations] = useState([]);
  const [editingEc, setEditingEc] = useState(null);
  const [showEcForm, setShowEcForm] = useState(false);

  // create-wizard state
  const [wizStep, setWizStep] = useState(1);
  const [pickedEc, setPickedEc] = useState(null);
  const [inlineName, setInlineName] = useState('');
  const [source, setSource] = useState(null);
  // Discount state per source. For OE-only mode we use oeDiscount; for
  // JP-only mode we use jpDiscount; for "mixed" we use both.
  const [oeDiscount, setOeDiscount] = useState(0);
  const [jpDiscount, setJpDiscount] = useState(0);
  const [lines, setLines] = useState([]);
  const [savingQuote, setSavingQuote] = useState(false);
  const [editingQuotationId, setEditingQuotationId] = useState(null);
  const fileRef = useRef(null);
  const [excelPreview, setExcelPreview] = useState(null);
  // For source="mixed" — which sub-catalogue the picker is currently on.
  const [mixedTab, setMixedTab] = useState('oe');
  // Live name of the master category powering the OE-MRP list (e.g.
  // "Eicher"). Falls back to "OE" until the API responds.
  const [oeMasterCategory, setOeMasterCategory] = useState('');

  // Refs into each line-row's qty input so we can auto-focus + select the
  // value as soon as a part is picked (single-tap → just type the qty).
  const qtyInputRefs = useRef({});
  const [focusLineIdx, setFocusLineIdx] = useState(null);
  useEffect(() => {
    if (focusLineIdx == null) return;
    const el = qtyInputRefs.current[focusLineIdx];
    if (el) {
      // requestAnimationFrame so the row is mounted before we focus
      requestAnimationFrame(() => { try { el.focus(); el.select(); } catch { /* ignore */ } });
    }
    setFocusLineIdx(null);
  }, [focusLineIdx, lines.length]);

  const reloadEcs = useCallback(async () => {
    try {
      const r = await axios.get(`${CQ}/my-customers`, { headers: { Authorization: `Bearer ${token}` } });
      setEndCustomers(r.data?.end_customers || []);
    } catch { /* ignore */ }
  }, [token]);

  const reloadQuotations = useCallback(async () => {
    try {
      const r = await axios.get(`${CQ}/my-quotations`, { headers: { Authorization: `Bearer ${token}` } });
      setMyQuotations(r.data?.quotations || []);
    } catch { /* ignore */ }
  }, [token]);

  useEffect(() => { if (token) { reloadEcs(); reloadQuotations(); } }, [token, reloadEcs, reloadQuotations]);

  // Fetch active master category once on mount so the wizard labels stay in
  // sync with the admin's master-category setting.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    axios.get(`${CQ}/oe-master-category`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { if (!cancelled) setOeMasterCategory(r.data?.name || ''); })
      .catch(() => { /* fall back to "OE" label */ });
    return () => { cancelled = true; };
  }, [token]);

  // When sub-customer or source changes, hydrate the discount fields from
  // the saved per-source defaults. Legacy `default_discount_pct` is honored
  // as a fallback so older records still pre-fill.
  useEffect(() => {
    if (!pickedEc || !source) return;
    const legacy = Number(pickedEc.default_discount_pct) || 0;
    const oe = pickedEc.default_oe_discount_pct != null ? Number(pickedEc.default_oe_discount_pct) || 0 : legacy;
    const jp = pickedEc.default_jp_discount_pct != null ? Number(pickedEc.default_jp_discount_pct) || 0 : legacy;
    if (source === 'oe') setOeDiscount(oe);
    else if (source === 'jp') setJpDiscount(jp);
    else if (source === 'mixed') { setOeDiscount(oe); setJpDiscount(jp); }
  }, [pickedEc, source]);

  const totals = useMemo(() => {
    const total_basic = lines.reduce((s, l) => s + (l.line_basic || 0), 0);
    const total_gst = lines.reduce((s, l) => s + (l.line_gst || 0), 0);
    const grand_total = lines.reduce((s, l) => s + (l.line_total || 0), 0);
    return { total_basic, total_gst, grand_total };
  }, [lines]);

  // Returns the discount % to apply for a given source_type.
  const discountFor = (st) => (st === 'oe' ? Number(oeDiscount) || 0 : Number(jpDiscount) || 0);

  const addPickedPart = (p, sourceTypeOverride = null) => {
    // For "mixed" quotations, pickers pass an explicit source type.
    // For OE/JP-only quotations, fall back to the wizard-level `source`.
    const sourceType = sourceTypeOverride || source;
    const disc = discountFor(sourceType);
    const mrp = p.mrp || 0;
    const sell = mrp * (1 - disc / 100);
    const basic = sell / 1.18;
    const gst_per_unit = sell - basic;
    const qty = 1;
    const partNo = sourceType === 'oe' ? p.oe_part_no : (p.new_jp_part_no || p.jp_part_no);
    if (lines.some((l) => (sourceType === 'oe' ? l.oe_part_no : l.jp_part_no) === partNo)) {
      toast.error('Already in list'); return;
    }
    const line = {
      source_type: sourceType,
      ...(sourceType === 'oe'
        ? { oe_part_no: p.oe_part_no, hsn_code: p.hsn_code || '' }
        : { jp_part_no: p.new_jp_part_no || p.jp_part_no, legacy_jp_part_no: p.jp_part_no, oe_part_no: p.oe_part_no || '', brand: p.brand || '', image: p.image || '' }),
      part_name: p.part_name || '',
      mrp, discount_pct: disc, sell_price: sell, basic_price: basic, gst_per_unit, quantity: qty,
      line_basic: basic * qty, line_gst: gst_per_unit * qty, line_total: sell * qty,
    };
    setLines((prev) => {
      // Focus + select the qty input of the newly added line so the user can
      // type the quantity immediately without an extra click.
      setFocusLineIdx(prev.length);
      return [...prev, line];
    });
  };

  const updateQty = (idx, qty) => {
    setLines((prev) => prev.map((l, i) => {
      if (i !== idx) return l;
      const q = Math.max(1, parseInt(qty, 10) || 1);
      return { ...l, quantity: q, line_basic: l.basic_price * q, line_gst: l.gst_per_unit * q, line_total: l.sell_price * q };
    }));
  };

  const removeLine = (idx) => setLines((p) => p.filter((_, i) => i !== idx));

  const resetWizard = () => {
    setWizStep(1); setPickedEc(null); setInlineName(''); setSource(null);
    setOeDiscount(0); setJpDiscount(0); setLines([]); setEditingQuotationId(null); setExcelPreview(null);
    setMixedTab('oe');
  };

  const saveQuotation = async () => {
    if (!lines.length) { toast.error('Add at least one item'); return; }
    setSavingQuote(true);
    try {
      const body = {
        end_customer_id: pickedEc?.end_customer_id,
        end_customer_inline: pickedEc ? null : (inlineName.trim() ? {
          name: inlineName.trim(),
          company_name: inlineName.trim(),
          default_oe_discount_pct: Number(oeDiscount) || 0,
          default_jp_discount_pct: Number(jpDiscount) || 0,
        } : null),
        source,
        oe_discount_pct: Number(oeDiscount) || 0,
        jp_discount_pct: Number(jpDiscount) || 0,
        // Legacy single-discount field — kept for back-compat. Server prefers
        // the per-source fields above.
        discount_pct: source === 'oe' ? (Number(oeDiscount) || 0) : (Number(jpDiscount) || 0),
        lines: lines.map((l) => ({
          source_type: l.source_type,
          oe_part_no: l.oe_part_no,
          jp_part_no: l.jp_part_no,
          part_name: l.part_name,
          quantity: l.quantity,
          mrp: l.mrp,
        })),
      };
      const url = editingQuotationId ? `${CQ}/my-quotations/${editingQuotationId}` : `${CQ}/create`;
      const fn = editingQuotationId ? axios.put : axios.post;
      const r = await fn(url, body, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(editingQuotationId ? 'Quotation updated' : `Quotation ${r.data?.quotation?.quotation_id || ''} created`);
      await reloadQuotations(); await reloadEcs();
      resetWizard();
      setTab('list');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    finally { setSavingQuote(false); }
  };

  const onExcelFile = async (file) => {
    if (!source) { toast.error('Pick OE or JP first'); return; }
    const buf = await file.arrayBuffer();
    const wb = XLSX.read(buf, { type: 'array' });
    const ws = wb.Sheets[wb.SheetNames[0]];
    const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
    try {
      // For mixed mode, we send the JP discount as the "default" but
      // each row's pricing is recomputed client-side on apply if needed.
      const excelDiscount = source === 'oe' ? oeDiscount : (source === 'jp' ? jpDiscount : Math.max(Number(oeDiscount) || 0, Number(jpDiscount) || 0));
      const r = await axios.post(`${CQ}/parse-excel`, { rows, source, discount_pct: excelDiscount }, { headers: { Authorization: `Bearer ${token}` } });
      setExcelPreview(r.data);
      toast.success(`Matched ${r.data.matched.length}, ambiguous ${r.data.ambiguous?.length || 0}, unmatched ${r.data.unmatched.length}`);
    } catch (e) { toast.error('Excel parse failed'); }
  };

  const applyExcelToLines = () => {
    if (!excelPreview) return;
    const next = [...lines];
    const seen = new Set(next.map((l) => l.source_type === 'oe' ? l.oe_part_no : l.jp_part_no));
    excelPreview.matched.forEach((m) => {
      const k = m.source_type === 'oe' ? m.oe_part_no : m.jp_part_no;
      if (!seen.has(k)) { next.push(m); seen.add(k); }
    });
    setLines(next);
    setExcelPreview(null);
    toast.success('Items added');
  };

  // For a JP-matched row in the Excel preview, look up the OE-list pricing
  // for its oe_part_no and swap the row to an OE entry. Lets the customer
  // override the default JP-first matching when they want OE-list pricing.
  const switchExcelRowToOe = async (idx) => {
    const row = excelPreview?.matched?.[idx];
    if (!row || row.source_type !== 'jp') return;
    if (!row.oe_part_no) { toast.error('This JP part has no OE reference to switch to'); return; }
    try {
      const r = await axios.post(`${CQ}/lookup-oe-pricing`, {
        oe_part_no: row.oe_part_no,
        quantity: row.quantity,
        discount_pct: Number(oeDiscount) || 0,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setExcelPreview((prev) => ({
        ...prev,
        matched: prev.matched.map((m, i) => (i === idx ? r.data : m)),
      }));
      toast.success(`Switched ${row.oe_part_no} to OE-list pricing`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'OE part not found');
    }
  };

  const switchExcelRowToJp = async (idx) => {
    // OE → JP swap is only sensible if a JP version actually exists. Use
    // the existing parse-excel pipeline with a single row to leverage the
    // JP fan-out logic (direct + OE→JP variants).
    const row = excelPreview?.matched?.[idx];
    if (!row || row.source_type !== 'oe') return;
    try {
      const r = await axios.post(`${CQ}/parse-excel`, {
        rows: [[row.oe_part_no, String(row.quantity)]],
        source: 'jp',
        discount_pct: Number(jpDiscount) || 0,
      }, { headers: { Authorization: `Bearer ${token}` } });
      const jpHit = (r.data?.matched || [])[0];
      const amb = (r.data?.ambiguous || [])[0];
      if (jpHit) {
        setExcelPreview((prev) => ({
          ...prev,
          matched: prev.matched.map((m, i) => (i === idx ? jpHit : m)),
        }));
        toast.success(`Switched to JP part ${jpHit.jp_part_no}`);
      } else if (amb) {
        // Drop this OE row and surface it as ambiguous so the chooser picks the variant.
        setExcelPreview((prev) => ({
          ...prev,
          matched: prev.matched.filter((_, i) => i !== idx),
          ambiguous: [...(prev.ambiguous || []), amb],
        }));
        toast.info('Multiple JP variants — pick one in the chooser above');
      } else {
        toast.error('No JP equivalent found');
      }
    } catch (e) { toast.error('Switch failed'); }
  };

  const downloadUnmatched = () => {
    if (!excelPreview?.unmatched?.length) return;
    const ws = XLSX.utils.aoa_to_sheet([['Part Number', 'Qty', 'Reason'], ...excelPreview.unmatched.map((u) => [u.part_no, u.quantity, u.reason])]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Unmatched');
    XLSX.writeFile(wb, `unmatched_${Date.now()}.xlsx`);
  };

  const printPdf = async (q) => { const doc = await buildQuotationPdf(q, { parent: user }); doc.autoPrint(); window.open(doc.output('bloburl'), '_blank'); };
  const downloadPdf = async (q) => { const doc = await buildQuotationPdf(q, { parent: user }); doc.save(`${q.quotation_id}.pdf`); };
  const downloadPdfWithImages = async (q) => {
    const hasJpLines = (q.lines || []).some((ln) => (ln.source_type === 'jp' || ln.jp_part_no) && ln.image);
    if (!hasJpLines) { toast.error('No JP parts with images in this quotation'); return; }
    toast.info('Generating PDF with watermarked images…');
    const doc = await buildQuotationPdf(q, { withImages: true, parent: user });
    doc.save(`${q.quotation_id}-images.pdf`);
  };

  const openEditQuotation = (q) => {
    setEditingQuotationId(q.quotation_id);
    setPickedEc({ end_customer_id: q.end_customer_id, name: q.end_customer_name, company_name: q.end_customer_company });
    setSource(q.source);
    const legacy = Number(q.discount_pct) || 0;
    setOeDiscount(q.oe_discount_pct != null ? Number(q.oe_discount_pct) || 0 : legacy);
    setJpDiscount(q.jp_discount_pct != null ? Number(q.jp_discount_pct) || 0 : legacy);
    setLines(q.lines || []);
    setWizStep(4); setTab('create');
  };

  const deleteQuotation = async (id) => {
    if (!window.confirm(`Delete ${id}?`)) return;
    try { await axios.delete(`${CQ}/my-quotations/${id}`, { headers: { Authorization: `Bearer ${token}` } }); toast.success('Deleted'); reloadQuotations(); }
    catch { toast.error('Failed to delete'); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 md:p-8" data-testid="customer-quotations-page">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <Sparkles className="w-7 h-7 text-amber-600" />
          <h1 className="text-2xl font-bold">Customer Convenience Panel</h1>
        </div>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-white">
            <TabsTrigger value="create" data-testid="cq-tab-create">Create Quotation</TabsTrigger>
            <TabsTrigger value="list" data-testid="cq-tab-list">My Quotations ({myQuotations.length})</TabsTrigger>
            <TabsTrigger value="customers" data-testid="cq-tab-customers">My Sub-Customers ({endCustomers.length})</TabsTrigger>
          </TabsList>

          {/* ── CREATE ── */}
          <TabsContent value="create" className="bg-white rounded-lg shadow p-4 mt-3">
            {wizStep === 1 && (
              <div className="space-y-3 max-w-lg">
                <h2 className="font-semibold">Step 1: Who is the quotation for?</h2>
                <Label>Sub-customer name</Label>
                <div className="relative">
                  <Search className="absolute left-2 top-2.5 w-4 h-4 text-gray-400 pointer-events-none" />
                  <Input
                    data-testid="cq-customer-typeahead"
                    placeholder="Type a name — pick from suggestions or create new"
                    className="pl-8"
                    value={pickedEc ? `${pickedEc.name} (${pickedEc.company_name})` : inlineName}
                    onChange={(e) => { setInlineName(e.target.value); setPickedEc(null); }}
                  />
                  {pickedEc && (
                    <button type="button" onClick={() => { setPickedEc(null); setInlineName(''); }} className="absolute right-2 top-2 text-gray-400 hover:text-red-600" data-testid="cq-customer-clear">
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
                {!pickedEc && inlineName.trim() && (() => {
                  const q = inlineName.trim().toLowerCase();
                  const matches = endCustomers.filter((ec) =>
                    (ec.name || '').toLowerCase().includes(q) ||
                    (ec.company_name || '').toLowerCase().includes(q) ||
                    (ec.mobile || '').includes(q)
                  ).slice(0, 8);
                  if (!matches.length) {
                    return <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">No match — pressing Next will create <span className="font-semibold">"{inlineName.trim()}"</span> as a new sub-customer.</div>;
                  }
                  return (
                    <div className="border rounded divide-y max-h-60 overflow-y-auto bg-white shadow-sm" data-testid="cq-typeahead-results">
                      {matches.map((ec) => (
                        <button
                          key={ec.end_customer_id}
                          type="button"
                          onClick={() => { setPickedEc(ec); setInlineName(''); }}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-amber-50"
                          data-testid={`cq-typeahead-pick-${ec.end_customer_id}`}
                        >
                          <div className="font-medium">{ec.name} <span className="text-xs text-gray-500">({ec.company_name})</span></div>
                          <div className="text-xs text-gray-500 flex gap-2 flex-wrap">
                            {ec.mobile && <span>{ec.mobile}</span>}
                            {[ec.city, ec.state].filter(Boolean).length > 0 && <span>· {[ec.city, ec.state].filter(Boolean).join(', ')}</span>}
                            {(ec.default_oe_discount_pct || ec.default_jp_discount_pct || ec.default_discount_pct) ? (
                              <span>· OE {ec.default_oe_discount_pct ?? ec.default_discount_pct ?? 0}% · JP {ec.default_jp_discount_pct ?? ec.default_discount_pct ?? 0}%</span>
                            ) : null}
                          </div>
                        </button>
                      ))}
                    </div>
                  );
                })()}
                <Button
                  data-testid="cq-step1-next"
                  onClick={() => {
                    if (!pickedEc && !inlineName.trim()) { toast.error('Type or pick a sub-customer'); return; }
                    setWizStep(2);
                  }}
                >
                  Next →
                </Button>
              </div>
            )}
            {wizStep === 2 && (
              <div className="space-y-3">
                <h2 className="font-semibold">Step 2: Pick the catalogue</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 max-w-4xl">
                  <button type="button" onClick={() => { setSource('oe'); setWizStep(3); }} className="border-2 rounded-lg p-4 text-left hover:border-amber-500 transition" data-testid="cq-source-oe">
                    <div className="font-semibold text-base">OEM (OE) parts only</div>
                    <p className="text-xs text-gray-600 mt-1">OE-MRP list{oeMasterCategory ? ` (${oeMasterCategory})` : ''}. 117K+ parts. No images.</p>
                  </button>
                  <button type="button" onClick={() => { setSource('jp'); setWizStep(3); }} className="border-2 rounded-lg p-4 text-left hover:border-amber-500 transition" data-testid="cq-source-jp">
                    <div className="font-semibold text-base">JP parts only</div>
                    <p className="text-xs text-gray-600 mt-1">Our full JP catalogue. Includes images. Customer discount applied.</p>
                  </button>
                  <button type="button" onClick={() => { setSource('mixed'); setWizStep(3); }} className="border-2 rounded-lg p-4 text-left hover:border-amber-500 transition relative" data-testid="cq-source-mixed">
                    <span className="absolute top-2 right-2 text-[10px] bg-amber-500 text-white px-1.5 py-0.5 rounded">NEW</span>
                    <div className="font-semibold text-base">Mixed{oeMasterCategory ? ` (${oeMasterCategory} + JP)` : ' (OE + JP)'}</div>
                    <p className="text-xs text-gray-600 mt-1">Search both catalogues in one quotation. Excel auto-routes each row — JP first, OE fallback.</p>
                  </button>
                </div>
                <Button variant="outline" onClick={() => setWizStep(1)}><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
              </div>
            )}
            {wizStep === 3 && (
              <div className="space-y-3 max-w-lg">
                <h2 className="font-semibold">Step 3: Sub-customer discount</h2>
                <p className="text-xs text-gray-600">Applied to MRP for every line. Saved per sub-customer so the next quotation pre-fills automatically.</p>
                {source === 'mixed' ? (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">OE catalogue %</Label>
                      <div className="flex items-center gap-1">
                        <Input data-testid="cq-discount-oe" type="number" min={0} max={99} step={0.5}
                          value={oeDiscount}
                          onChange={(e) => setOeDiscount(Number(e.target.value) || 0)} className="w-28" />
                        <span>%</span>
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">JP catalogue %</Label>
                      <div className="flex items-center gap-1">
                        <Input data-testid="cq-discount-jp" type="number" min={0} max={99} step={0.5}
                          value={jpDiscount}
                          onChange={(e) => setJpDiscount(Number(e.target.value) || 0)} className="w-28" />
                        <span>%</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Input data-testid="cq-discount" type="number" min={0} max={99} step={0.5}
                      value={source === 'oe' ? oeDiscount : jpDiscount}
                      onChange={(e) => {
                        const v = Number(e.target.value) || 0;
                        if (source === 'oe') setOeDiscount(v); else setJpDiscount(v);
                      }}
                      className="w-32" />
                    <span>%</span>
                  </div>
                )}
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setWizStep(2)}><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
                  <Button data-testid="cq-step3-next" onClick={() => setWizStep(4)}>Next →</Button>
                </div>
              </div>
            )}
            {wizStep === 4 && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2 space-y-3">
                  <div className="flex justify-between items-center">
                    <h2 className="font-semibold">{editingQuotationId ? `Editing ${editingQuotationId}` : 'Step 4: Add items'} ({source === 'mixed' ? 'MIXED' : source?.toUpperCase()})</h2>
                    <div className="flex items-center gap-2">
                      <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" data-testid="cq-excel-input" onChange={(e) => { if (e.target.files?.[0]) onExcelFile(e.target.files[0]); e.target.value = ''; }} />
                      <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="cq-excel-btn"><UploadCloud className="w-4 h-4 mr-1" /> Import Excel</Button>
                    </div>
                  </div>
                  {source === 'mixed' ? (
                    <div className="space-y-2">
                      <div className="inline-flex bg-gray-100 rounded p-1 text-xs">
                        <button type="button" onClick={() => setMixedTab('oe')} data-testid="cq-mixed-tab-oe" className={`px-3 py-1 rounded transition ${mixedTab === 'oe' ? 'bg-white shadow text-amber-700 font-semibold' : 'text-gray-600'}`}>Search OE catalogue</button>
                        <button type="button" onClick={() => setMixedTab('jp')} data-testid="cq-mixed-tab-jp" className={`px-3 py-1 rounded transition ${mixedTab === 'jp' ? 'bg-white shadow text-amber-700 font-semibold' : 'text-gray-600'}`}>Search JP catalogue</button>
                      </div>
                      <PartPicker key={mixedTab} source={mixedTab} discountPct={mixedTab === 'oe' ? oeDiscount : jpDiscount} onPicked={(p) => addPickedPart(p, mixedTab)} token={token} />
                    </div>
                  ) : (
                    <PartPicker source={source} discountPct={source === 'oe' ? oeDiscount : jpDiscount} onPicked={addPickedPart} token={token} />
                  )}
                  {excelPreview && (
                    <div className="border rounded p-3 bg-amber-50/60 space-y-3" data-testid="cq-excel-preview">
                      <div className="flex justify-between items-center">
                        <div className="font-semibold text-sm">Excel Preview</div>
                        <button onClick={() => setExcelPreview(null)} className="text-gray-500"><X className="w-4 h-4" /></button>
                      </div>
                      <div className="text-xs flex flex-wrap gap-x-3 gap-y-1">
                        <span className="px-1.5 py-0.5 rounded bg-green-100 text-green-700">✅ Matched: {excelPreview.matched.length}</span>
                        <span>· JP {excelPreview.matched.filter((m) => m.source_type === 'jp').length}</span>
                        <span>· OE {excelPreview.matched.filter((m) => m.source_type === 'oe').length}</span>
                        {!!excelPreview.ambiguous?.length && <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">⚠️ Ambiguous: {excelPreview.ambiguous.length}</span>}
                        {!!excelPreview.unmatched.length && <span className="px-1.5 py-0.5 rounded bg-red-100 text-red-700">❌ Unmatched: {excelPreview.unmatched.length}</span>}
                      </div>

                      {/* Matched items review table — grouped by source with switch buttons */}
                      {!!excelPreview.matched.length && (
                        <div className="bg-white border rounded overflow-hidden">
                          <table className="w-full text-xs">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="p-2 text-left w-20">As</th>
                                <th className="p-2 text-left">Part No</th>
                                <th className="p-2 text-left">Name</th>
                                <th className="p-2 text-right">Qty</th>
                                <th className="p-2 text-right">Sell</th>
                                <th className="p-2 text-right">Total</th>
                                <th className="p-2 text-right w-28">Switch</th>
                              </tr>
                            </thead>
                            <tbody>
                              {excelPreview.matched.map((m, i) => {
                                const isJp = m.source_type === 'jp';
                                return (
                                  <tr key={i} className="border-t" data-testid={`cq-preview-row-${i}`}>
                                    <td className="p-2">
                                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${isJp ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                                        {isJp ? 'JP' : 'OE'}
                                      </span>
                                    </td>
                                    <td className="p-2 font-mono">{isJp ? m.jp_part_no : m.oe_part_no}</td>
                                    <td className="p-2 truncate max-w-[180px]">{m.part_name}</td>
                                    <td className="p-2 text-right">{m.quantity}</td>
                                    <td className="p-2 text-right">{money(m.sell_price)}</td>
                                    <td className="p-2 text-right font-semibold">{money(m.line_total)}</td>
                                    <td className="p-2 text-right">
                                      {isJp && m.oe_part_no ? (
                                        <button
                                          type="button"
                                          onClick={() => switchExcelRowToOe(i)}
                                          data-testid={`cq-switch-to-oe-${i}`}
                                          className="text-[10px] text-blue-700 hover:underline"
                                          title={`Use OE-list price for ${m.oe_part_no}`}
                                        >
                                          → Use OE
                                        </button>
                                      ) : !isJp ? (
                                        <button
                                          type="button"
                                          onClick={() => switchExcelRowToJp(i)}
                                          data-testid={`cq-switch-to-jp-${i}`}
                                          className="text-[10px] text-green-700 hover:underline"
                                          title="Try JP catalogue"
                                        >
                                          → Use JP
                                        </button>
                                      ) : null}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {!!excelPreview.ambiguous?.length && (
                        <div className="space-y-2">
                          {excelPreview.ambiguous.map((amb, i) => (
                            <div key={i} className="border rounded p-2 bg-white text-xs">
                              <div className="font-mono font-bold mb-1">{amb.oe_part_no} — qty {amb.quantity} — pick a JP variant:</div>
                              <select className="border rounded p-1 w-full text-xs" data-testid={`cq-ambig-${amb.oe_part_no}`} onChange={(e) => {
                                const idx = Number(e.target.value);
                                if (Number.isNaN(idx)) return;
                                const opt = amb.options[idx];
                                const matched = { source_type: 'jp', ...opt, quantity: amb.quantity, line_basic: opt.basic_price * amb.quantity, line_gst: opt.gst_per_unit * amb.quantity, line_total: opt.sell_price * amb.quantity };
                                setExcelPreview((prev) => ({ ...prev, matched: [...prev.matched, matched], ambiguous: prev.ambiguous.filter((_, j) => j !== i) }));
                              }}>
                                <option value="">— select —</option>
                                {amb.options.map((o, j) => <option key={j} value={j}>{o.jp_part_no} · {o.part_name?.substring(0, 30)} · {money(o.sell_price)}</option>)}
                              </select>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="flex gap-2 items-center flex-wrap">
                        {!!excelPreview.unmatched.length && (
                          <Button size="sm" variant="outline" onClick={downloadUnmatched} data-testid="cq-download-unmatched"><Download className="w-4 h-4 mr-1" /> Download Unmatched ({excelPreview.unmatched.length})</Button>
                        )}
                        <Button size="sm" onClick={applyExcelToLines} data-testid="cq-apply-excel" disabled={!excelPreview.matched.length}>Apply matched items</Button>
                      </div>
                    </div>
                  )}
                  <div className="border rounded">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 text-xs">
                        <tr><th className="p-2 text-left">Part No</th><th className="p-2 text-left">Name</th><th className="p-2 text-right">MRP</th><th className="p-2 text-right">Sell</th><th className="p-2 text-right">Qty</th><th className="p-2 text-right">Total</th><th></th></tr>
                      </thead>
                      <tbody>
                        {lines.map((l, i) => (
                          <tr key={i} className="border-t text-xs" data-testid={`cq-line-${i}`}>
                            <td className="p-2 font-mono">{l.source_type === 'oe' ? l.oe_part_no : l.jp_part_no}</td>
                            <td className="p-2 truncate max-w-[160px]">{l.part_name}</td>
                            <td className="p-2 text-right text-gray-400 line-through">{money(l.mrp)}</td>
                            <td className="p-2 text-right font-semibold">{money(l.sell_price)}</td>
                            <td className="p-2 text-right"><Input ref={(el) => { if (el) qtyInputRefs.current[i] = el; else delete qtyInputRefs.current[i]; }} type="number" min={1} className="h-7 w-16 text-right" value={l.quantity} onChange={(e) => updateQty(i, e.target.value)} onFocus={(e) => e.target.select()} data-testid={`cq-qty-${i}`} /></td>
                            <td className="p-2 text-right font-bold">{money(l.line_total)}</td>
                            <td className="p-2"><button onClick={() => removeLine(i)} className="text-red-600"><Trash2 className="w-3.5 h-3.5" /></button></td>
                          </tr>
                        ))}
                        {!lines.length && <tr><td colSpan={7} className="p-4 text-center text-xs text-gray-500">No items yet — search above or import Excel</td></tr>}
                      </tbody>
                    </table>
                  </div>
                </div>
                <aside className="bg-amber-50 border border-amber-300 rounded p-4 space-y-2 h-fit sticky top-4">
                  <div className="font-semibold text-amber-900">Quotation Summary</div>
                  <div className="text-xs text-amber-800">For: <span className="font-medium">{pickedEc?.company_name || inlineName || '—'}</span></div>
                  <div className="text-xs text-amber-800">Source: <span className="font-medium">{source === 'mixed' ? 'MIXED' : source?.toUpperCase()}</span>{source === 'mixed' ? <> · OE <span className="font-medium">{oeDiscount}%</span> · JP <span className="font-medium">{jpDiscount}%</span></> : <> · Discount: <span className="font-medium">{source === 'oe' ? oeDiscount : jpDiscount}%</span></>}</div>
                  <hr />
                  <div className="flex justify-between text-xs"><span>Total Basic</span><span>{money(totals.total_basic)}</span></div>
                  <div className="flex justify-between text-xs"><span>GST @ 18%</span><span>{money(totals.total_gst)}</span></div>
                  <div className="flex justify-between font-bold text-amber-900 text-base"><span>Grand Total</span><span>{money(totals.grand_total)}</span></div>
                  <div className="flex flex-col gap-2 pt-2">
                    <Button onClick={saveQuotation} disabled={savingQuote || !lines.length} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="cq-save-quotation">
                      {savingQuote && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} {editingQuotationId ? 'Update Quotation' : 'Save Quotation'}
                    </Button>
                    <Button variant="outline" size="sm" onClick={resetWizard}>Cancel</Button>
                  </div>
                </aside>
              </div>
            )}
          </TabsContent>

          {/* ── LIST ── */}
          <TabsContent value="list" className="bg-white rounded-lg shadow p-4 mt-3">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs">
                <tr><th className="p-2 text-left">No</th><th className="p-2 text-left">Date</th><th className="p-2 text-left">Customer</th><th className="p-2 text-left">Source</th><th className="p-2 text-right">Items</th><th className="p-2 text-right">Total</th><th className="p-2 text-right">Actions</th></tr>
              </thead>
              <tbody>
                {myQuotations.map((q) => (
                  <tr key={q.quotation_id} className="border-t" data-testid={`cq-row-${q.quotation_id}`}>
                    <td className="p-2 font-mono">{q.quotation_id}</td>
                    <td className="p-2 text-xs">{new Date(q.created_at).toLocaleDateString('en-IN')}</td>
                    <td className="p-2">{q.end_customer_company || q.end_customer_name}</td>
                    <td className="p-2"><span className={`text-[10px] px-1.5 py-0.5 rounded ${q.source === 'oe' ? 'bg-blue-100 text-blue-700' : q.source === 'mixed' ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-700'}`}>{q.source?.toUpperCase()}</span></td>
                    <td className="p-2 text-right">{q.lines?.length || 0}</td>
                    <td className="p-2 text-right font-semibold">{money(q.grand_total)}</td>
                    <td className="p-2 text-right">
                      <div className="inline-flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => openEditQuotation(q)} title="Edit"><Edit2 className="w-3.5 h-3.5" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => printPdf(q)} title="Print"><Printer className="w-3.5 h-3.5" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => downloadPdf(q)} title="Download PDF"><Share2 className="w-3.5 h-3.5" /></Button>
                        {(q.source === 'jp' || q.source === 'mixed') && (q.lines || []).some((ln) => (ln.source_type === 'jp' || ln.jp_part_no) && ln.image) && (
                          <Button size="sm" variant="ghost" onClick={() => downloadPdfWithImages(q)} title="Download PDF with watermarked images" data-testid={`cq-pdf-images-${q.quotation_id}`}>
                            <Package className="w-3.5 h-3.5 text-amber-700" />
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => deleteQuotation(q.quotation_id)} title="Delete"><Trash2 className="w-3.5 h-3.5 text-red-600" /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!myQuotations.length && <tr><td colSpan={7} className="p-6 text-center text-gray-500 text-sm">No quotations yet. Create one above.</td></tr>}
              </tbody>
            </table>
          </TabsContent>

          {/* ── CUSTOMERS ── */}
          <TabsContent value="customers" className="bg-white rounded-lg shadow p-4 mt-3">
            <div className="flex justify-end mb-3"><Button onClick={() => { setEditingEc(null); setShowEcForm(true); }} data-testid="cq-add-ec-btn"><Plus className="w-4 h-4 mr-1" /> Add Sub-Customer</Button></div>
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs"><tr><th className="p-2 text-left">Name</th><th className="p-2 text-left">Company</th><th className="p-2 text-left">Mobile</th><th className="p-2 text-left">Location</th><th className="p-2 text-right">OE Disc.</th><th className="p-2 text-right">JP Disc.</th><th className="p-2 text-right">Actions</th></tr></thead>
              <tbody>
                {endCustomers.map((ec) => (
                  <tr key={ec.end_customer_id} className="border-t" data-testid={`cq-ec-${ec.end_customer_id}`}>
                    <td className="p-2">{ec.name}</td>
                    <td className="p-2">{ec.company_name}</td>
                    <td className="p-2">{ec.mobile || '—'}</td>
                    <td className="p-2 text-xs">{[ec.city, ec.state, ec.pin_code].filter(Boolean).join(', ') || '—'}</td>
                    <td className="p-2 text-right">{ec.default_oe_discount_pct ?? ec.default_discount_pct ?? 0}%</td>
                    <td className="p-2 text-right">{ec.default_jp_discount_pct ?? ec.default_discount_pct ?? 0}%</td>
                    <td className="p-2 text-right">
                      <Button size="sm" variant="ghost" onClick={() => { setEditingEc(ec); setShowEcForm(true); }}><Edit2 className="w-3.5 h-3.5" /></Button>
                      <Button size="sm" variant="ghost" onClick={async () => { if (!window.confirm('Delete this sub-customer?')) return; await axios.delete(`${CQ}/my-customers/${ec.end_customer_id}`, { headers: { Authorization: `Bearer ${token}` } }); reloadEcs(); }}><Trash2 className="w-3.5 h-3.5 text-red-600" /></Button>
                    </td>
                  </tr>
                ))}
                {!endCustomers.length && <tr><td colSpan={7} className="p-6 text-center text-gray-500 text-sm">No sub-customers in your address book yet.</td></tr>}
              </tbody>
            </table>
            {showEcForm && <EndCustomerForm initial={editingEc} token={token} onClose={() => setShowEcForm(false)} onSaved={() => reloadEcs()} />}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
